import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bot, Save, Sparkles } from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

const PROVIDERS = [
  { v: 'openai',     label: 'OpenAI Realtime' },
  { v: 'anthropic',  label: 'Anthropic Claude' },
  { v: 'google',     label: 'Google Gemini' },
  { v: 'deepgram',   label: 'Deepgram Nova-3' },
];

export default function AiAgents() {
  const qc = useQueryClient();
  const toast = useToast();
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });
  const [domain, setDomain] = useState('');
  const active = domain || tenants.data?.[0]?.domain_uuid || '';

  const list = useQuery<any[]>({
    queryKey: ['ai-agents', active],
    queryFn: () => fJson(`/api/ai-agents?domain_uuid=${active}`),
    enabled: !!active,
  });
  const users = useQuery<any[]>({ queryKey: ['users-list'], queryFn: () => fJson('/api/users') });

  const [selectedExt, setSelectedExt] = useState<string>('');
  useEffect(() => { if (!selectedExt && list.data?.[0]) setSelectedExt(list.data[0].extension_uuid); }, [list.data, selectedExt]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold inline-flex items-center gap-2"><Bot size={20} /> AI Agents</h1>
        <select className="input" value={active} onChange={e => setDomain(e.target.value)}>
          {(tenants.data || []).map(t => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
        </select>
      </div>
      <p className="text-sm text-textSecondary">
        Per-extension AI agents — instead of ringing a phone, route to an LLM-driven conversation.
        Pick a provider, write the system prompt, set spam/abuse handling, and the agent answers when calls hit that extension.
      </p>

      <div className="grid grid-cols-[300px_1fr] gap-4">
        <div className="card p-2 space-y-1">
          {(users.data || []).filter((u: any) => u.domain_uuid === active).map((u: any) => {
            const cfg = list.data?.find((a: any) => a.extension_uuid === u.extension_uuid);
            return (
              <button key={u.extension_uuid} onClick={() => setSelectedExt(u.extension_uuid)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 ${selectedExt === u.extension_uuid ? 'bg-primary text-white' : 'hover:bg-elevated'}`}>
                <span className="flex-1">{u.extension} — {u.name}</span>
                {cfg?.enabled ? <Sparkles size={12} className="text-success" /> : null}
              </button>
            );
          })}
        </div>
        {selectedExt && <AgentForm extension_uuid={selectedExt} qc={qc} toast={toast} />}
      </div>
    </div>
  );
}

function AgentForm({ extension_uuid, qc, toast }: any) {
  const cfg = useQuery<any>({
    queryKey: ['ai-agent', extension_uuid],
    queryFn: () => fJson(`/api/ai-agents/${extension_uuid}`),
  });
  const [form, setForm] = useState<any>({});
  useEffect(() => { if (cfg.data) setForm(cfg.data); }, [cfg.data?.extension_uuid]);
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });
  const save = useMutation({
    mutationFn: () => fJson(`/api/ai-agents/${extension_uuid}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ai-agent', extension_uuid] }); qc.invalidateQueries({ queryKey: ['ai-agents'] }); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });

  return (
    <div className="card p-4 space-y-4">
      <label className="text-sm flex items-center gap-2 font-medium">
        <input type="checkbox" checked={!!form.enabled} onChange={e => set('enabled', e.target.checked)} />
        Route this extension to an AI agent
      </label>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Provider">
          <select className="input" value={form.provider || 'openai'} onChange={e => set('provider', e.target.value)}>
            {PROVIDERS.map(p => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
        </Field>
        <Field label="Model">
          <input className="input" value={form.model || ''} onChange={e => set('model', e.target.value)}
            placeholder={form.provider === 'openai' ? 'gpt-4o-realtime-preview' : form.provider === 'anthropic' ? 'claude-sonnet-4-6' : ''} />
        </Field>
        <Field label="Voice"><input className="input" value={form.voice || ''} onChange={e => set('voice', e.target.value)} placeholder="alloy / verse / etc." /></Field>
        <Field label="Language"><input className="input" value={form.language || 'en-US'} onChange={e => set('language', e.target.value)} /></Field>
        <Field label="Max call duration (s)">
          <input type="number" className="input" min={60} max={3600} value={form.max_call_duration_seconds || 600} onChange={e => set('max_call_duration_seconds', parseInt(e.target.value) || 600)} />
        </Field>
        <Field label="API key secret name">
          <input className="input font-mono" value={form.api_key_secret_name || ''} onChange={e => set('api_key_secret_name', e.target.value)} placeholder="OPENAI_API_KEY_TENANT_XYZ" />
        </Field>
      </div>
      <Field label="System prompt">
        <textarea className="input font-mono text-xs" rows={6} value={form.system_prompt || ''} onChange={e => set('system_prompt', e.target.value)}
          placeholder="You are a helpful receptionist for Acme Dental. Answer questions about hours, location, and book appointments. If asked about medical advice, transfer to a clinician." />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Initial greeting">
          <input className="input" value={form.initial_greeting || ''} onChange={e => set('initial_greeting', e.target.value)} placeholder="Hi! Thanks for calling Acme. How can I help?" />
        </Field>
        <Field label="End-call detection phrase">
          <input className="input" value={form.end_call_phrase || ''} onChange={e => set('end_call_phrase', e.target.value)} placeholder="goodbye / thanks bye" />
        </Field>
        <Field label="Handoff to extension">
          <input className="input" value={form.handoff_to_extension || ''} onChange={e => set('handoff_to_extension', e.target.value)} placeholder="1000" />
        </Field>
        <Field label="Handoff trigger phrase">
          <input className="input" value={form.handoff_phrase || ''} onChange={e => set('handoff_phrase', e.target.value)} placeholder="speak to a human" />
        </Field>
      </div>

      <div>
        <div className="font-medium text-sm border-b border-border pb-1 mb-2">Spam & Abuse Handling</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Spam action">
            <select className="input" value={form.spam_action || 'continue'} onChange={e => set('spam_action', e.target.value)}>
              <option value="continue">Continue conversation</option>
              <option value="reject">Reject call</option>
              <option value="transfer">Transfer to extension</option>
            </select>
          </Field>
          <Field label="Spam transfer target"><input className="input" value={form.spam_transfer_target || ''} onChange={e => set('spam_transfer_target', e.target.value)} disabled={form.spam_action !== 'transfer'} /></Field>
          <Field label="Abuse action">
            <select className="input" value={form.abuse_action || 'warn'} onChange={e => set('abuse_action', e.target.value)}>
              <option value="warn">Warn caller</option>
              <option value="reject">Reject + hangup</option>
              <option value="transfer">Transfer to extension</option>
            </select>
          </Field>
          <Field label="Abuse transfer target"><input className="input" value={form.abuse_transfer_target || ''} onChange={e => set('abuse_transfer_target', e.target.value)} disabled={form.abuse_action !== 'transfer'} /></Field>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Toggle label="Transcribe calls"  v={form.transcribe_calls !== false}  on={v => set('transcribe_calls', v)} />
        <Toggle label="Summarize calls"   v={form.summarize_calls !== false}   on={v => set('summarize_calls', v)} />
      </div>

      <div className="flex justify-end">
        <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-1"><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

function Field({ label, children }: any) { return <div><label className="block text-sm text-textSecondary mb-1">{label}</label>{children}</div>; }
function Toggle({ label, v, on }: any) { return <label className="text-sm flex items-center gap-2 py-1"><input type="checkbox" checked={v} onChange={e => on(e.target.checked)} /> {label}</label>; }
